/* iter213 — Revenue Integrity Audit (READ-ONLY).
   Starts from EVERY appointment; classifies each into one bucket, traces its
   lifecycle, and reconciles cross-screen counts. Repair actions are shown but
   DISABLED until the read-only audit + a separate repair plan are approved. */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { AlertTriangle, CheckCircle2, Clock, FileText, ShieldQuestion, Ban, Lock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUCKET_META = {
  stuck_before_completion: { label: 'Jobs Stuck Before Completion', icon: Clock, color: 'text-red-300', border: 'border-red-500/40' },
  completed_missing_disposition: { label: 'Completed — Missing Revenue Disposition', icon: ShieldQuestion, color: 'text-amber-300', border: 'border-amber-500/40' },
  decision_without_record: { label: 'Billing Decision Without Billing Record', icon: AlertTriangle, color: 'text-orange-300', border: 'border-orange-500/40' },
  office_review: { label: 'Office Review', icon: FileText, color: 'text-sky-300', border: 'border-sky-500/40' },
  valid_no_invoice: { label: 'Valid No-Invoice Outcomes', icon: CheckCircle2, color: 'text-emerald-300', border: 'border-emerald-500/40' },
  invoiced: { label: 'Invoiced (Healthy)', icon: CheckCircle2, color: 'text-[#D4AF37]', border: 'border-[#D4AF37]/40' },
  excluded: { label: 'Excluded (Cancelled / Draft)', icon: Ban, color: 'text-[#737373]', border: 'border-[#262626]' },
};

const REPAIR_ACTIONS = {
  stuck_before_completion: ['Open Shift', 'Create Missing Completion'],
  completed_missing_disposition: ['View Completion', 'Set Billing Outcome'],
  decision_without_record: ['View Completion', 'Create Missing Office-Review Draft', 'Link Existing Invoice', 'Return to Billing Missing'],
  office_review: ['Open Office Billing Review', 'View Invoice'],
  valid_no_invoice: ['View Completion'],
  invoiced: ['View Invoice'],
  excluded: [],
};

const RANGES = [['today', 'Today'], ['this_week', 'This Week'], ['custom', 'Custom'], ['all', 'All History']];

const RecTile = ({ label, value, accent = 'text-white', testid }) => (
  <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-2.5" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-wider text-[#737373]">{label}</p>
    <p className={`text-lg font-bold ${accent}`}>{value}</p>
  </div>
);

const RevenueIntegrityAudit = () => {
  const [range, setRange] = useState('this_week');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ range });
      if (range === 'custom') { if (fromDate) params.set('from_date', fromDate); if (toDate) params.set('to_date', toDate); }
      const { data: d } = await axios.get(`${API}/admin/revenue-integrity-audit?${params}`, { withCredentials: true });
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load audit');
    }
    setLoading(false);
  }, [range, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  const rec = data?.reconciliation || {};
  const invMismatch = data && (rec.customer_invoices + rec.warranty_invoices) !== rec.invoice_list_count_for_range;

  return (
    <div data-testid="revenue-integrity-audit" className="space-y-5">
      <div className="bg-[#141414] border border-[#D4AF37]/30 rounded-lg p-3 flex items-start gap-2">
        <Lock className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />
        <p className="text-xs text-[#C9C9C9]">
          <span className="text-[#D4AF37] font-bold">Read-only audit.</span> Every scheduled job is traced from creation to invoice.
          Repair actions are disabled until a repair plan is approved — nothing here creates, alters, or deletes records.
        </p>
      </div>

      {/* Range controls */}
      <div className="flex items-end gap-2 flex-wrap">
        <div className="flex gap-1">
          {RANGES.map(([k, l]) => (
            <button key={k} data-testid={`ria-range-${k}`} onClick={() => setRange(k)}
              className={`px-3 py-1.5 rounded text-xs font-bold border ${range === k ? 'bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37]' : 'border-[#262626] text-white hover:bg-[#262626]'}`}>{l}</button>
          ))}
        </div>
        {range === 'custom' && (
          <>
            <div><label className="text-[10px] uppercase text-[#737373]">From</label><Input type="date" data-testid="ria-from" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white h-9 mt-1" /></div>
            <div><label className="text-[10px] uppercase text-[#737373]">To</label><Input type="date" data-testid="ria-to" value={toDate} onChange={(e) => setToDate(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white h-9 mt-1" /></div>
          </>
        )}
        <Button data-testid="ria-refresh" size="sm" onClick={load} disabled={loading} className="bg-[#262626] text-white hover:bg-[#333] h-9">{loading ? 'Scanning…' : 'Run Audit'}</Button>
      </div>

      {loading && !data && <p className="text-[#A3A3A3]">Scanning every appointment…</p>}

      {data && (
        <>
          {/* Cross-screen reconciliation */}
          <div>
            <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold mb-2">Cross-Screen Reconciliation {data.date_from ? `(${data.date_from} → ${data.date_to})` : '(all history)'}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
              <RecTile label="Appointments" value={rec.total_appointments} testid="ria-rec-appts" />
              <RecTile label="Claimed" value={rec.claimed} />
              <RecTile label="In Progress" value={rec.in_progress} accent={rec.in_progress ? 'text-amber-300' : 'text-white'} />
              <RecTile label="Completions" value={rec.completions} />
              <RecTile label="Billing Decisions" value={rec.billing_decisions} />
              <RecTile label="Office-Review Drafts" value={rec.office_review_drafts} />
              <RecTile label="Customer Invoices" value={rec.customer_invoices} accent="text-[#D4AF37]" />
              <RecTile label="Warranty Invoices" value={rec.warranty_invoices} />
              <RecTile label="Finalized" value={rec.finalized_invoices} />
              <RecTile label="Voided" value={rec.voided_invoices} />
              <RecTile label="Missing Billing" value={rec.missing_billing_records} accent={rec.missing_billing_records ? 'text-red-300' : 'text-white'} testid="ria-rec-missing" />
              <RecTile label="Invoices in DB (range)" value={rec.invoice_list_count_for_range} testid="ria-rec-invlist" />
            </div>
            {invMismatch && (
              <div className="mt-2 bg-red-500/10 border border-red-500/50 rounded-md p-2.5 text-red-200 text-xs flex items-center gap-2" data-testid="ria-inv-mismatch">
                <AlertTriangle className="w-4 h-4 shrink-0" /> {rec.invoice_list_count_for_range} invoice(s) exist in the range but only {rec.customer_invoices + rec.warranty_invoices} link back to an audited appointment — unlinked/orphan invoices explain All-Invoices vs diagnostics count gaps.
              </div>
            )}
          </div>

          {/* Buckets */}
          <div className="space-y-4">
            {Object.entries(data.buckets).map(([key, rows]) => {
              const meta = BUCKET_META[key];
              if (!meta) return null;
              const Icon = meta.icon;
              return (
                <div key={key} data-testid={`ria-bucket-${key}`} className={`border rounded-lg ${meta.border} bg-[#0A0A0A]`}>
                  <div className="flex items-center gap-2 px-3 py-2 border-b border-[#262626]">
                    <Icon className={`w-4 h-4 ${meta.color}`} />
                    <span className={`text-sm font-bold ${meta.color}`}>{meta.label}</span>
                    <span className="ml-auto text-xs text-[#A3A3A3]">{rows.length}</span>
                  </div>
                  {rows.length === 0 ? (
                    <p className="text-[#525252] text-xs px-3 py-2">None.</p>
                  ) : (
                    <div className="divide-y divide-[#1a1a1a]">
                      {rows.map((r) => (
                        <button key={r.appointment_id} data-testid={`ria-row-${r.appointment_id}`} onClick={() => setDetail(r)}
                          className="w-full text-left px-3 py-2 hover:bg-[#141414] transition-colors">
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <span className="text-white text-sm font-medium">{r.customer_name || 'Unknown customer'}{r.work_order_number ? ` · WO ${r.work_order_number}` : ''}</span>
                            <span className="text-[#A3A3A3] text-xs">{r.service_date || '—'} · {r.technician}</span>
                          </div>
                          <p className="text-[11px] text-[#737373] mt-0.5">{r.flag_reason}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Read-only job trace drawer */}
      <Sheet open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <SheetContent data-testid="ria-detail-drawer" className="bg-[#141414] border-[#262626] text-white w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-white flex items-center gap-2"><FileText className="w-5 h-5 text-[#D4AF37]" /> Job Trace</SheetTitle>
          </SheetHeader>
          {detail && (
            <div className="space-y-3 mt-4 text-sm">
              <div className="bg-[#0A0A0A] border border-[#262626] rounded p-3 space-y-1">
                <p className="text-white font-bold">{detail.customer_name || 'Unknown'}</p>
                <p className="text-[#A3A3A3] text-xs">WO {detail.work_order_number || '—'} · {detail.service_type || '—'} · {detail.service_date || '—'}</p>
                <p className="text-[#A3A3A3] text-xs">Tech: {detail.technician}{detail.helpers?.length ? ` · Helpers: ${detail.helpers.join(', ')}` : ''}</p>
              </div>
              <div className="bg-red-500/10 border border-red-500/40 rounded p-2.5 text-red-200 text-xs" data-testid="ria-detail-reason">
                <b>Flagged:</b> {detail.flag_reason}
              </div>
              {[
                ['Appointment status', detail.appointment_status],
                ['Claimed at', detail.claimed_at],
                ['On job at', detail.in_progress_at],
                ['Completed at', detail.completed_at],
                ['Completion record', detail.completion_id ? `Yes (${detail.completion_id})` : 'None'],
                ['Billing outcome', detail.invoice_status || 'None'],
                ['Office-review draft', detail.office_review_draft_id || 'None'],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3 border-b border-[#1a1a1a] pb-1">
                  <span className="text-[#737373] text-xs">{k}</span>
                  <span className="text-white text-xs text-right break-all">{v || '—'}</span>
                </div>
              ))}
              <div>
                <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold mb-1">Linked invoices ({detail.linked_invoices?.length || 0})</p>
                {(detail.linked_invoices || []).length === 0 ? (
                  <p className="text-[#737373] text-xs">No invoices linked to this appointment.</p>
                ) : detail.linked_invoices.map((i) => (
                  <div key={i.id} className="bg-[#0A0A0A] border border-[#262626] rounded p-2 text-xs mb-1">
                    <span className="text-white font-bold">{i.invoice_number || i.id}</span> · ${Number(i.amount || 0).toFixed(2)} · {i.status}
                    {i.payor_kind === 'warranty' && <span className="text-sky-300"> · warranty</span>}
                    {i.office_review_status && <span className="text-[#A3A3A3]"> · {i.office_review_status}</span>}
                  </div>
                ))}
              </div>
              <div className="pt-2 border-t border-[#262626]">
                <p className="text-[10px] uppercase tracking-wider text-[#737373] mb-2 flex items-center gap-1"><Lock className="w-3 h-3" /> Recommended repairs (disabled until approved)</p>
                <div className="flex flex-wrap gap-2">
                  {(REPAIR_ACTIONS[detail.bucket] || []).map((a) => (
                    <Button key={a} data-testid={`ria-repair-${a.replace(/\s+/g, '-').toLowerCase()}`} disabled size="sm" variant="outline" className="border-[#262626] text-[#737373] opacity-50 cursor-not-allowed h-8">{a}</Button>
                  ))}
                  {(REPAIR_ACTIONS[detail.bucket] || []).length === 0 && <span className="text-[#525252] text-xs">No repair needed.</span>}
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default RevenueIntegrityAudit;
